import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

export const FAQS = [
  {
    q: "What is your minimum order quantity (MOQ)?",
    a: "There is no rigid MOQ for standard sizes — we supply from a single piece for trials up to bulk tonnage orders. For non-standard or special-material fasteners, MOQ depends on the raw material bar size available, and we will confirm it along with the quotation.",
  },
  {
    q: "What are your typical lead times?",
    a: "Standard stud bolts, hex bolts and nuts in common grades are usually dispatched in 3–7 working days. Custom lengths, special grades or coated fasteners typically take 10–15 working days. Urgent plant-shutdown requirements can be expedited — mention the deadline in your enquiry.",
  },
  {
    q: "Which standards and grades do you manufacture to?",
    a: "We manufacture to ASTM / ASME, BS, DIN, JIS and BS EN ISO standards, as well as to client drawings and samples. Common grades include ASTM A193 B7 / B16 / B8 / B8M studs, ASTM A194 2H / 4 / 7 / 8 / 8M nuts, IS 1367 property classes 8.8 / 10.9 / 12.9 and stainless steel 304, 316, 321 and duplex on request.",
  },
  {
    q: "What size range can you produce?",
    a: "Stud bolts and threaded rods from M12 to M100 (½\" to 4\") in any length, hex bolts and nuts across matching sizes, and special large-diameter fasteners on enquiry. Threads can be supplied as UNC, UNF, metric coarse or metric fine.",
  },
  {
    q: "Do you manufacture against custom drawings?",
    a: "Yes. A large share of our work is made to client drawings, samples or sketches — including non-standard heads, undercut and stepped studs, tie rods, long threaded assemblies and bespoke machined components. Send your drawing with the RFQ and we will revert with a manufacturing feasibility and price.",
  },
  {
    q: "Do you provide test certificates?",
    a: "Every batch is supplied with a mill test certificate (MTC) covering chemical composition and mechanical properties. Additional third-party inspection, PMI, hardness testing, ultrasonic testing and dimensional inspection reports can be arranged when specified in the purchase order.",
  },
  {
    q: "What coatings and finishes are available?",
    a: "Self colour / black oxide, hot dip galvanising, zinc electroplating, PTFE (Xylan) coating, phosphating and cadmium plating are all available. Coating thickness and colour can be specified to your standard.",
  },
  {
    q: "Do you supply outside Gujarat and export?",
    a: "Yes. We dispatch across India — Gujarat, Maharashtra, Rajasthan, Madhya Pradesh, Delhi NCR, Tamil Nadu, Karnataka, West Bengal and the North East — and we support export orders with sea-worthy packing and the documentation your freight forwarder requires.",
  },
  {
    q: "Can I get a sample before placing a bulk order?",
    a: "Yes. Samples of standard items can be sent for approval; for custom items a first-article sample can be produced and submitted for your dimensional and material sign-off before bulk production starts.",
  },
  {
    q: "What are your payment terms?",
    a: "Terms are agreed case-by-case: advance for first-time and custom orders, and agreed credit terms for repeat customers and approved vendors. GST invoicing and e-way bills are provided as standard.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" className="relative bg-mist py-20 lg:py-28" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-4"
        >
          <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
            <span className="w-10 h-[2px] bg-brand" />
            Buyer FAQ
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
            Questions
            <br />
            purchase teams
            <br />
            ask us
          </h2>
          <p className="mt-6 text-sm text-slate-600 leading-relaxed max-w-sm">
            Everything about MOQ, lead time, grades, certificates and dispatch —
            answered up front. If your question is not here, call
            <a href="tel:+918000151777" className="text-brand font-semibold hover:underline ml-1" data-testid="faq-phone-link">
              8000151777
            </a>
            .
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-8"
        >
          <Accordion type="single" collapsible className="divide-y divide-steel-light border-y border-steel-light">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-none">
                <AccordionTrigger
                  className="text-left py-5 gap-6 hover:no-underline group"
                  data-testid={`faq-trigger-${i + 1}`}
                >
                  <span className="flex items-start gap-4">
                    <span className="font-sans font-semibold text-[11px] text-brand pt-1 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display font-bold uppercase text-sm sm:text-base text-ink leading-snug group-hover:text-brand transition-colors duration-300">
                      {f.q}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pl-10 pr-2" data-testid={`faq-answer-${i + 1}`}>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
